import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { preferencesService } from '../services/api/preferencesService';
import { 
  UserPreferences, 
  UpdatePreferencesRequest, 
  Watchlist, 
  CreateWatchlistRequest,
  DEFAULT_PREFERENCES 
} from '../types/preferences';
import { useSession } from '../components/session/SessionManager';
import { isApiError } from '../services/api/client';

interface PreferencesContextType {
  // Preferences state
  preferences: UserPreferences | null;
  watchlists: Watchlist[];
  defaultWatchlist: Watchlist | null;
  loading: boolean;
  error: string | null;

  // Preferences methods
  loadPreferences: () => Promise<void>;
  updatePreferences: (updates: UpdatePreferencesRequest) => Promise<void>;
  resetPreferences: () => Promise<void>;

  // Watchlist methods
  loadWatchlists: () => Promise<void>;
  createWatchlist: (data: CreateWatchlistRequest) => Promise<Watchlist>;
  updateWatchlist: (id: string, data: Partial<CreateWatchlistRequest>) => Promise<void>;
  deleteWatchlist: (id: string) => Promise<void>;
  addToWatchlist: (watchlistId: string, instrumentId: string) => Promise<void>;
  removeFromWatchlist: (watchlistId: string, instrumentId: string) => Promise<void>;
  setDefaultWatchlist: (watchlistId: string) => Promise<void>;

  // Convenience methods
  getTradingPreferences: () => UserPreferences['trading'];
  getUIPreferences: () => UserPreferences['ui'];
  getChartPreferences: () => UserPreferences['chart'];
  getNotificationPreferences: () => UserPreferences['notifications'];
  
  updateTradingPreferences: (updates: Partial<UserPreferences['trading']>) => Promise<void>;
  updateUIPreferences: (updates: Partial<UserPreferences['ui']>) => Promise<void>;
  updateChartPreferences: (updates: Partial<UserPreferences['chart']>) => Promise<void>;
  updateNotificationPreferences: (updates: Partial<UserPreferences['notifications']>) => Promise<void>;
}

const PreferencesContext = createContext<PreferencesContextType | undefined>(undefined);

export const usePreferences = () => {
  const context = useContext(PreferencesContext);
  if (!context) {
    throw new Error('usePreferences must be used within a PreferencesProvider');
  }
  return context;
};

interface PreferencesProviderProps {
  children: ReactNode;
}

export const PreferencesProvider: React.FC<PreferencesProviderProps> = ({ children }) => {
  const session = useSession();
  
  // State
  const [preferences, setPreferences] = useState<UserPreferences | null>(null);
  const [watchlists, setWatchlists] = useState<Watchlist[]>([]);
  const [defaultWatchlist, setDefaultWatchlistState] = useState<Watchlist | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load preferences from API
  const loadPreferences = useCallback(async () => {
    if (!session.isAuthenticated) return;

    try {
      setLoading(true);
      setError(null);
      
      const userPreferences = await preferencesService.getUserPreferences();
      setPreferences(userPreferences);
    } catch (err) {
      console.error('Error loading preferences:', err);
      if (isApiError(err)) {
        setError(`Failed to load preferences: ${err.message}`);
      } else {
        setError('Failed to load preferences');
      }
      
      // Use default preferences if API fails
      setPreferences({
        userId: session.currentAccount?.accountId || 'default',
        ...DEFAULT_PREFERENCES,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      });
    } finally {
      setLoading(false);
    }
  }, [session.isAuthenticated, session.currentAccount?.accountId]);

  // Update preferences
  const updatePreferences = useCallback(async (updates: UpdatePreferencesRequest) => {
    try {
      setLoading(true);
      setError(null);
      
      const updatedPreferences = await preferencesService.updatePreferences(updates);
      setPreferences(updatedPreferences);
    } catch (err) {
      console.error('Error updating preferences:', err);
      if (isApiError(err)) {
        setError(`Failed to update preferences: ${err.message}`);
      } else {
        setError('Failed to update preferences');
      }
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  // Reset preferences to defaults
  const resetPreferences = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const resetPreferences = await preferencesService.resetPreferences();
      setPreferences(resetPreferences);
    } catch (err) {
      console.error('Error resetting preferences:', err);
      if (isApiError(err)) {
        setError(`Failed to reset preferences: ${err.message}`);
      } else {
        setError('Failed to reset preferences');
      }
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  // Load watchlists
  const loadWatchlists = useCallback(async () => {
    if (!session.isAuthenticated) return;

    try {
      const userWatchlists = await preferencesService.getWatchlists();
      setWatchlists(userWatchlists);
      
      const defaultWL = userWatchlists.find(w => w.isDefault) || null;
      setDefaultWatchlistState(defaultWL);
    } catch (err) {
      console.error('Error loading watchlists:', err);
      // Don't set error for watchlists, just use empty array
      setWatchlists([]);
      setDefaultWatchlistState(null);
    }
  }, [session.isAuthenticated]);

  // Create watchlist
  const createWatchlist = useCallback(async (data: CreateWatchlistRequest): Promise<Watchlist> => {
    try {
      const newWatchlist = await preferencesService.createWatchlist(data);
      setWatchlists(prev => [...prev, newWatchlist]);
      
      if (data.isDefault || watchlists.length === 0) {
        setDefaultWatchlistState(newWatchlist);
      }
      
      return newWatchlist;
    } catch (err) {
      console.error('Error creating watchlist:', err);
      throw err;
    }
  }, [watchlists.length]);

  // Update watchlist
  const updateWatchlist = useCallback(async (id: string, data: Partial<CreateWatchlistRequest>) => {
    try {
      const updatedWatchlist = await preferencesService.updateWatchlist(id, data);
      setWatchlists(prev => prev.map(w => w.id === id ? updatedWatchlist : w));
      
      if (updatedWatchlist.isDefault) {
        setDefaultWatchlistState(updatedWatchlist);
      }
    } catch (err) {
      console.error('Error updating watchlist:', err);
      throw err;
    }
  }, []);

  // Delete watchlist
  const deleteWatchlist = useCallback(async (id: string) => {
    try {
      await preferencesService.deleteWatchlist(id);
      setWatchlists(prev => prev.filter(w => w.id !== id));
      
      if (defaultWatchlist?.id === id) {
        const remaining = watchlists.filter(w => w.id !== id);
        setDefaultWatchlistState(remaining.length > 0 ? remaining[0] : null);
      }
    } catch (err) {
      console.error('Error deleting watchlist:', err);
      throw err;
    }
  }, [defaultWatchlist?.id, watchlists]);

  // Add to watchlist
  const addToWatchlist = useCallback(async (watchlistId: string, instrumentId: string) => {
    try {
      await preferencesService.addToWatchlist(watchlistId, { instrumentId });
      // Refresh watchlists to get updated data
      await loadWatchlists();
    } catch (err) {
      console.error('Error adding to watchlist:', err);
      throw err;
    }
  }, [loadWatchlists]);

  // Remove from watchlist
  const removeFromWatchlist = useCallback(async (watchlistId: string, instrumentId: string) => {
    try {
      await preferencesService.removeFromWatchlist(watchlistId, instrumentId);
      // Refresh watchlists to get updated data
      await loadWatchlists();
    } catch (err) {
      console.error('Error removing from watchlist:', err);
      throw err;
    }
  }, [loadWatchlists]);

  // Set default watchlist
  const setDefaultWatchlist = useCallback(async (watchlistId: string) => {
    try {
      await preferencesService.setDefaultWatchlist(watchlistId);
      
      // Update local state
      setWatchlists(prev => prev.map(w => ({ ...w, isDefault: w.id === watchlistId })));
      const newDefault = watchlists.find(w => w.id === watchlistId);
      if (newDefault) {
        setDefaultWatchlistState(newDefault);
      }
    } catch (err) {
      console.error('Error setting default watchlist:', err);
      throw err;
    }
  }, [watchlists]);

  // Convenience getters
  const getTradingPreferences = useCallback(() => {
    return preferences?.trading || DEFAULT_PREFERENCES.trading;
  }, [preferences]);

  const getUIPreferences = useCallback(() => {
    return preferences?.ui || DEFAULT_PREFERENCES.ui;
  }, [preferences]);

  const getChartPreferences = useCallback(() => {
    return preferences?.chart || DEFAULT_PREFERENCES.chart;
  }, [preferences]);

  const getNotificationPreferences = useCallback(() => {
    return preferences?.notifications || DEFAULT_PREFERENCES.notifications;
  }, [preferences]);

  // Convenience updaters
  const updateTradingPreferences = useCallback(async (updates: Partial<UserPreferences['trading']>) => {
    await updatePreferences({ trading: updates });
  }, [updatePreferences]);

  const updateUIPreferences = useCallback(async (updates: Partial<UserPreferences['ui']>) => {
    await updatePreferences({ ui: updates });
  }, [updatePreferences]);

  const updateChartPreferences = useCallback(async (updates: Partial<UserPreferences['chart']>) => {
    await updatePreferences({ chart: updates });
  }, [updatePreferences]);

  const updateNotificationPreferences = useCallback(async (updates: Partial<UserPreferences['notifications']>) => {
    await updatePreferences({ notifications: updates });
  }, [updatePreferences]);

  // Load data when user authenticates
  useEffect(() => {
    if (session.isAuthenticated && session.currentAccount) {
      loadPreferences();
      loadWatchlists();
    } else {
      // Clear data when user logs out
      setPreferences(null);
      setWatchlists([]);
      setDefaultWatchlistState(null);
      setError(null);
    }
  }, [session.isAuthenticated, session.currentAccount?.accountId, loadPreferences, loadWatchlists]);

  const value: PreferencesContextType = {
    // State
    preferences,
    watchlists,
    defaultWatchlist,
    loading,
    error,

    // Methods
    loadPreferences,
    updatePreferences,
    resetPreferences,
    loadWatchlists,
    createWatchlist,
    updateWatchlist,
    deleteWatchlist,
    addToWatchlist,
    removeFromWatchlist,
    setDefaultWatchlist,

    // Convenience methods
    getTradingPreferences,
    getUIPreferences,
    getChartPreferences,
    getNotificationPreferences,
    updateTradingPreferences,
    updateUIPreferences,
    updateChartPreferences,
    updateNotificationPreferences
  };

  return (
    <PreferencesContext.Provider value={value}>
      {children}
    </PreferencesContext.Provider>
  );
};

export default PreferencesProvider;
